// Экран «Общее» (владелец 2026-09-15, #96): настройки и сводка проекта целиком.
// Старт проекта (переехал сюда из шапки «Модели ГПР») и сводка по этапам работ — начало,
// окончание, длительность, сколько задач закончено. Даты — сохранённые, после «Актуализировать».
// Календари с праздниками — отдельной задачей после обсуждения (правка ядра расчёта, #62).
import { useCallback, useEffect, useState } from 'react';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';
import { fmt, parseDate } from '../util';

type Svodka = Data<'/api/projects/{pid}/etapy-svodka', 'get'>;
type Project = Data<'/api/projects', 'get'>[number];
type Prazdniki = Data<'/api/projects/{pid}/prazdniki', 'get'>;

const DNI = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export function Obshchee({ ctx }: { ctx: ShellCtx }) {
  const [svodka, setSvodka] = useState<Svodka | null>(null);
  const [start, setStart] = useState('');
  const [aktual, setAktual] = useState<string | null>(null);
  const [prazdniki, setPrazdniki] = useState<Prazdniki | null>(null);
  const [gruzim, setGruzim] = useState(false);
  const can = ctx.canPlan;

  // праздники грузятся отдельно: первый показ года может сходить на xmlcalendar.ru
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<Prazdniki>('get', p('/api/projects/{pid}/prazdniki', { pid: ctx.pid }));
      if (zhiv && r.ok) setPrazdniki(r.data);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);

  /** «Обновить праздники»: перезапросить календарь, например когда вышло постановление */
  async function obnovitPrazdniki() {
    setGruzim(true);
    const r = await call<Prazdniki>('post', p('/api/projects/{pid}/prazdniki/obnovit', { pid: ctx.pid }), {});
    setGruzim(false);
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    setPrazdniki(r.data);
    const tk = r.data.gody.filter(g => g.istochnik === 'tk').map(g => g.god);
    ctx.onSaved?.('Праздники обновлены' + (tk.length
      ? '. На ' + tk.join(', ') + ' постановления ещё нет — показаны праздники по Трудовому кодексу' : ''));
  }

  const load = useCallback(async () => {
    const [s, pr] = await Promise.all([
      call<Svodka>('get', p('/api/projects/{pid}/etapy-svodka', { pid: ctx.pid })),
      call<Project[]>('get', '/api/projects'),
    ]);
    if (s.ok) setSvodka(s.data); else ctx.onError?.(s.error.message);
    if (pr.ok) {
      const cur = pr.data.find(x => x.id === ctx.pid);
      setStart(cur?.start ? fmt(cur.start) : '');
      setAktual(cur?.aktual ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  /** старт проекта: раньше него не начинается ни одна задача */
  async function saveStart() {
    const iso = start.trim() === '' ? null : parseDate(start);
    if (iso === undefined) { ctx.onError?.('Дата старта не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const r = await call<{ data_starta: string | null }>('patch', p('/api/projects/{pid}', { pid: ctx.pid }),
      { data_starta: iso });
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    ctx.onSaved?.('Старт проекта сохранён. Нажмите «Пересчитать» в модели, чтобы обновить даты.');
    void load();
  }

  const stroki = svodka?.stroki || [];
  const dd = (x?: string | null) => (x ? fmt(x) : '—');
  const nach = stroki.map(s => s.nachalo).filter(Boolean).sort()[0] || null;
  const okon = stroki.map(s => s.okonchanie).filter(Boolean).sort().slice(-1)[0] || null;
  const vsego = stroki.reduce((a, s) => a + s.zadach, 0);
  const zav = stroki.reduce((a, s) => a + s.zaversheno, 0);
  const dni = (a: string | null, b: string | null) =>
    (a && b ? Math.round((Date.parse(b) - Date.parse(a)) / 86400000) : null);
  const aktualTekst = aktual ? new Date(aktual).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
    : 'не пересчитывался';

  return (
    <div>
      <div className="pw-section">Проект</div>
      <div className="pw-card ob-proekt">
        <label className="ob-pole" title="Старт проекта: раньше этой даты не начинается ни одна задача">
          <span className="wc-lab">Старт проекта</span>
          <input value={start} placeholder="ДД.ММ.ГГГГ" disabled={!can} style={{ width: 130 }}
            onChange={e => setStart(e.target.value)} onBlur={() => void saveStart()}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        </label>
        <div className="ob-pole">
          <span className="wc-lab">Даты пересчитаны</span>
          <span>{aktualTekst}</span>
        </div>
      </div>

      <div className="pw-section">Этапы работ</div>
      <table className="pw-table ob-etapy">
        <thead>
          <tr>
            <th>Этап</th>
            <th className="num">Работ</th>
            <th className="num">Задач</th>
            <th className="num">Начало</th>
            <th className="num">Окончание</th>
            <th className="num">Длит., дн</th>
            <th className="num" title="Задач, законченных по факту">Закончено</th>
          </tr>
        </thead>
        <tbody>
          {!svodka && <tr><td colSpan={7} className="muted">Загрузка…</td></tr>}
          {svodka && !stroki.length && <tr><td colSpan={7} className="muted">Задач в проекте нет.</td></tr>}
          {stroki.map(s => (
            <tr key={s.etap}>
              <td>{s.etap}</td>
              <td className="num">{s.rabot}</td>
              <td className="num">{s.zadach}</td>
              <td className="num">{dd(s.nachalo)}</td>
              <td className="num">{dd(s.okonchanie)}</td>
              <td className="num">{s.dlit ?? '—'}</td>
              <td className="num">{s.zaversheno}{s.zadach ? ' (' + Math.round(s.zaversheno * 100 / s.zadach) + ' %)' : ''}</td>
            </tr>
          ))}
          {stroki.length > 1 && (
            <tr className="ob-itog">
              <td>Весь проект</td>
              <td className="num">{stroki.reduce((a, s) => a + s.rabot, 0)}</td>
              <td className="num">{vsego}</td>
              <td className="num">{dd(nach)}</td>
              <td className="num">{dd(okon)}</td>
              <td className="num">{dni(nach, okon) ?? '—'}</td>
              <td className="num">{zav}{vsego ? ' (' + Math.round(zav * 100 / vsego) + ' %)' : ''}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="pw-muted" style={{ marginTop: 8 }}>
        Даты — по последнему пересчёту модели. Этап работы задаётся в справочнике «Работы».
      </div>

      {/* государственные праздники за сроки проекта (владелец 2026-09-15, #62) */}
      <div className="pw-section ob-prazd-h">
        <span>Праздники в сроках проекта</span>
        {prazdniki?.s && <span className="pw-muted">{dd(prazdniki.s)} — {dd(prazdniki.po)}</span>}
        {can && <button className="sec" disabled={gruzim}
          title="Перезапросить производственный календарь с xmlcalendar.ru — например, когда вышло постановление на следующий год"
          onClick={() => void obnovitPrazdniki()}>{gruzim ? 'Загружаю…' : 'Обновить праздники'}</button>}
      </div>
      {prazdniki && prazdniki.gody.some(g => g.istochnik === 'tk') && (
        <div className="pw-muted" style={{ marginBottom: 6 }}>
          На {prazdniki.gody.filter(g => g.istochnik === 'tk').map(g => g.god).join(', ')} постановления
          о переносах ещё нет — показаны праздники по Трудовому кодексу; переносы появятся после публикации.
        </div>
      )}
      <table className="pw-table ob-etapy ob-prazd">
        <thead>
          <tr><th className="num">Дата</th><th className="num">День</th><th>Праздник</th></tr>
        </thead>
        <tbody>
          {!prazdniki && <tr><td colSpan={3} className="muted">Загрузка…</td></tr>}
          {prazdniki && !prazdniki.s && (
            <tr><td colSpan={3} className="muted">У проекта нет дат: задайте старт или пересчитайте модель.</td></tr>)}
          {prazdniki && prazdniki.s && !prazdniki.stroki.length && (
            <tr><td colSpan={3} className="muted">Праздников в сроках проекта нет.</td></tr>)}
          {prazdniki?.stroki.map(x => (
            <tr key={x.data} className={x.perenos ? 'ob-perenos' : ''}>
              <td className="num">{dd(x.data)}</td>
              <td className="num">{DNI[new Date(x.data + 'T00:00:00Z').getUTCDay()]}</td>
              <td>{x.nazvanie}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pw-muted" style={{ marginTop: 8 }}>
        Нерабочие праздничные дни по постановлению правительства, с переносами выходных
        (источник — производственный календарь xmlcalendar.ru). Учитываются в сроках работ
        с календарём «Без праздников» (справочник работ, группа «Расчёт»).
      </div>
    </div>
  );
}
